import * as React from 'react';
import { Translate } from '../../../../../../../shared/LanguageService';
import { EditSellerInfo } from './EditSellerInfo';
import { ViewSellerInfo } from './ViewSellerInfo';
import {
    createEmailAction,
    createReferenceAction,
    invoiceSellerReducer
} from '../state/invoiceSeller';
import { useMutation, useQueryClient } from 'react-query';
import { InvoiceSellerApi } from '../query/InvoiceSellerApi';
import { InvoiceInformationQueryCacheKey } from './InvoiceInformation';

type InvoiceSellerLabels = {
    receiver: string;
    reference: string;
    sendTo: string;
    save: string;
    cancel: string;
    edit: string;
};

type TranslatedInvoiceSellerProps = {
    email: string;
    reference: string;
    receiverTranslationKey: string;
    onSaveReference: (reference: string) => void;
    onSaveEmail: (email: string) => void;
    canEdit: boolean;
};

type ExternalProps = {
    email: string;
    reference: string;
    receiverTranslationKey: string;
    orderId: number;
    canEdit: boolean;
};

type InvoiceSellerProps = {
    email: string;
    reference: string;
    receiver: string;
    labels: InvoiceSellerLabels;
    onSaveReference: (reference: string) => void;
    onSaveEmail: (email: string) => void;
    canEdit: boolean;
};

export type InvoiceSellerState = {
    isEditingReference: boolean;
    isEditingEmail: boolean;
    reference: string;
    email: string;
};

export enum ActionEditField {
    Email = 'isEditingEmail',
    Reference = 'isEditingReference'
}

export type InvoiceSellerAction = {
    type: 'TOGGLE_EDIT';
    field: ActionEditField.Reference | ActionEditField.Email;
    reference?: string;
    email?: string;
};

let initialState = (email: string, reference: string) => ({
    isEditingReference: false,
    isEditingEmail: false,
    email,
    reference
});

export function InvoiceSeller({
    email,
    reference,
    labels,
    receiver,
    onSaveReference,
    onSaveEmail,
    canEdit
}: InvoiceSellerProps) {
    let [state, dispatch] = React.useReducer(invoiceSellerReducer, initialState(email, reference));

    return (
        <dl>
            <dt>{labels.receiver}</dt>
            <dd>{receiver}</dd>
            <dt>{labels.reference}</dt>
            {state.isEditingReference ? (
                <EditSellerInfo
                    tooltipCancel={labels.cancel}
                    tooltipSave={labels.save}
                    value={state.reference}
                    onCancel={() => {
                        dispatch(createReferenceAction(state.reference));
                    }}
                    onSave={(value) => {
                        onSaveReference(value);
                        dispatch(createReferenceAction(value));
                    }}
                />
            ) : (
                <ViewSellerInfo
                    tooltipEdit={labels.edit}
                    value={state.reference}
                    canEdit={canEdit}
                    onEdit={() => {
                        dispatch(createReferenceAction(state.reference));
                    }}
                />
            )}
            <dt>{labels.sendTo}</dt>
            {state.isEditingEmail ? (
                <EditSellerInfo
                    tooltipCancel={labels.cancel}
                    tooltipSave={labels.save}
                    value={state.email}
                    onCancel={() => {
                        dispatch(createEmailAction(state.email));
                    }}
                    onSave={(value) => {
                        onSaveEmail(value);
                        dispatch(createEmailAction(value));
                    }}
                />
            ) : (
                <ViewSellerInfo
                    tooltipEdit={labels.edit}
                    value={state.email}
                    canEdit={canEdit}
                    onEdit={() => {
                        dispatch(createEmailAction(state.email));
                    }}
                />
            )}
        </dl>
    );
}

function TranslatedInvoiceSeller({
    email,
    reference,
    receiverTranslationKey,
    onSaveReference,
    onSaveEmail,
    canEdit
}: TranslatedInvoiceSellerProps) {
    let labels: InvoiceSellerLabels = {
        reference: Translate('view-order/basic-order-info/invoice-info/reference'),
        receiver: Translate('view-order/basic-order-info/invoice-info/receiver'),
        sendTo: Translate('view-order/basic-order-info/invoice-info/send-to'),
        save: Translate('view-order/basic-order-info/saveLinkButton'),
        cancel: Translate('view-order-collection/title/button-label/cancel'),
        edit: Translate('view-order/basic-order-info/order-info/button-labels/edit')
    };

    return (
        <InvoiceSeller
            email={email}
            receiver={Translate(receiverTranslationKey)}
            reference={reference}
            labels={labels}
            onSaveReference={onSaveReference}
            onSaveEmail={onSaveEmail}
            canEdit={canEdit}
        />
    );
}

export default function InvoiceSellerDataLayer({
    email,
    reference,
    receiverTranslationKey,
    orderId,
    canEdit
}: ExternalProps) {
    let queryClient = useQueryClient();

    let saveSellerReferenceMutation = useMutation(
        ({ reference, orderId }: { reference: string; orderId: number }) =>
            InvoiceSellerApi.saveReference(orderId, reference),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(InvoiceInformationQueryCacheKey);
            }
        }
    );

    let saveSellerEmailMutation = useMutation(
        ({ email, orderId }: { email: string; orderId: number }) =>
            InvoiceSellerApi.saveEmail(orderId, email),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(InvoiceInformationQueryCacheKey);
            }
        }
    );

    function handleSaveReference(reference: string) {
        // @ts-ignore
        saveSellerReferenceMutation.mutateAsync({
            reference,
            orderId
        });
    }

    function handleSaveEmail(email: string) {
        // @ts-ignore
        saveSellerEmailMutation.mutateAsync({
            email,
            orderId
        });
    }

    return (
        <TranslatedInvoiceSeller
            email={email}
            reference={reference}
            receiverTranslationKey={receiverTranslationKey}
            onSaveReference={handleSaveReference}
            onSaveEmail={handleSaveEmail}
            canEdit={canEdit}
        />
    );
}
